/**
 * Per-user "should notifications make a sound" preference.
 *
 * Mirrors `app-shell/lib/shellPrefs.ts` exactly — same per-user keying for the same reason: the auth
 * store persists to `localStorage`, which is shared across tabs and survives a tab closing, so a
 * single global key would hand the next person to sign in on this machine someone else's setting.
 *
 * ⚠️ Sound defaults ON. That is a deliberate choice and the arguable one: it is the only signal that
 * reaches someone working in another window, which is the entire point of adding it. The toggle sits
 * in the panel footer beside the desktop-push switch so anyone it bothers can find it in one place,
 * and the tones are quiet and under 300ms.
 */

const SOUND_PREFIX = 'dop.notif.sound.';

export const SOUND_ENABLED_DEFAULT = true;

function keyFor(userId: number | null): string | null {
  return userId === null ? null : `${SOUND_PREFIX}${userId}`;
}

/**
 * Every access is guarded: `localStorage` THROWS (not returns null) in some privacy modes and
 * embedded webviews, and an unguarded read on the shell's render path white-screens the app.
 */
export function readSoundEnabled(userId: number | null): boolean {
  const key = keyFor(userId);
  if (key === null) return SOUND_ENABLED_DEFAULT;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return SOUND_ENABLED_DEFAULT;
    return raw === 'true';
  } catch {
    return SOUND_ENABLED_DEFAULT;
  }
}

/** Silently a no-op when storage is unavailable — the setting just does not survive the session. */
export function writeSoundEnabled(userId: number | null, enabled: boolean): void {
  const key = keyFor(userId);
  if (key === null) return;
  try {
    localStorage.setItem(key, String(enabled));
  } catch {
    /* Storage unavailable. */
  }
}
